import React, { useState, useEffect } from 'react';
import { BarChart2Icon, ClockIcon, BrainCircuitIcon, AlertCircleIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

// Componentes
import { OverviewTab } from '../components/OverviewTab.tsx';
import { HorariosTab } from '../components/HorariosTab.tsx';
import { AiVsHumanoTab } from '../components/AiVsHumanoTab.tsx';
import { LoadingState } from '../components/LoadingState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import MonthlyCalendarFilter, { MonthlyFilterType } from '../components/MonthlyCalendarFilter.tsx';

// Hooks e Utils
import { useDashboardData } from '../hooks/useDashboardData.ts';
import { getFirstDayOfCurrentMonth, getFirstDayOfPreviousMonth } from '../utils/dateUtils.ts';
import { getFunnelMetrics, FunnelMetricsParams } from '../services/metricsApi.ts';

// Interfaces para dados de período
interface PeriodData {
  leads: number;
  agendamentos: number;
  comparecimentos: number;
  vendas: number;
}

interface PeriodComparisonData {
  mesAtual: PeriodData;
  mesAnterior: PeriodData;
}

const DashboardMetrics: React.FC = () => {
  const { isDark } = useTheme();
  // Estados para filtros
  const [activeTab, setActiveTab] = useState<number>(0);

  // Estado para o filtro de período
  const [currentFilter, setCurrentFilter] = useState<MonthlyFilterType>('thisMonth');
  const [startDate, setStartDate] = useState<string>(getFirstDayOfCurrentMonth().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Estado para comparação de períodos (será atualizado com dados reais)
  const [periodComparison, setPeriodComparison] = useState<PeriodComparisonData>({
    mesAtual: {
      leads: 0,
      agendamentos: 0,
      comparecimentos: 0,
      vendas: 0,
    },
    mesAnterior: {
      leads: 0,
      agendamentos: 0,
      comparecimentos: 0,
      vendas: 0,
    }
  });

  // Carregar dados usando o hook
  const {
    funnelMetrics,
    timeseriesData,
    aiVsHumanoData,
    timeDistribution,
    weeklyHeatmap,
    loading,
    error,
    clinicId,
    clinicName
  } = useDashboardData({
    startDate,
    endDate
  });

  // Manipulador para mudança de filtro
  const handleFilterSelect = (
    filterType: MonthlyFilterType,
    newStartDate?: string,
    newEndDate?: string
  ) => {
    setCurrentFilter(filterType);

    // Se for filtro personalizado, usamos as datas passadas
    if (filterType === 'custom' && newStartDate && newEndDate) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      return;
    }

    // Para outros tipos de filtro, calculamos as datas
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (filterType) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;

      case 'last3Months':
        start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        break;

      case 'last6Months':
        start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        break;

      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        break;

      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Carregar dados de comparação de períodos (mês atual vs mês anterior)
  useEffect(() => {
    const loadPeriodComparison = async () => {
      if (!clinicId) return;

      try {
        // Obter o primeiro dia do mês atual e o dia atual para o período atual
        const today = new Date();
        const currentMonthStart = getFirstDayOfCurrentMonth();

        // Obter o mesmo período do mês passado
        const previousMonthStart = getFirstDayOfPreviousMonth();
        const previousMonthSameDay = new Date(previousMonthStart);
        previousMonthSameDay.setDate(today.getDate());

        // Formatar datas para API
        const currentStartStr = currentMonthStart.toISOString().split('T')[0];
        const currentEndStr = today.toISOString().split('T')[0];
        const previousStartStr = previousMonthStart.toISOString().split('T')[0];
        const previousEndStr = previousMonthSameDay.toISOString().split('T')[0];

        // Parâmetros para as chamadas API
        const currentParams: FunnelMetricsParams = {
          clinicId,
          startDate: currentStartStr,
          endDate: currentEndStr
        };

        const previousParams: FunnelMetricsParams = {
          clinicId,
          startDate: previousStartStr,
          endDate: previousEndStr
        };

        // Fazer chamadas em paralelo
        const [currentData, previousData] = await Promise.all([
          getFunnelMetrics(currentParams),
          getFunnelMetrics(previousParams)
        ]);

        // Atualizar o estado com os dados reais
        setPeriodComparison({
          mesAtual: {
            leads: currentData.totalLeads,
            agendamentos: currentData.totalAgendamentos,
            comparecimentos: currentData.totalComparecimentos,
            vendas: currentData.totalVendas
          },
          mesAnterior: {
            leads: previousData.totalLeads,
            agendamentos: previousData.totalAgendamentos,
            comparecimentos: previousData.totalComparecimentos,
            vendas: previousData.totalVendas
          }
        });
      } catch (err) {
        console.error("Erro ao carregar comparação de períodos:", err);
      }
    };

    loadPeriodComparison();
  }, [clinicId]);

  // Renderização condicional para estado de carregamento
  if (loading) {
    return <LoadingState message="Carregando métricas do dashboard..." />;
  }

  // Renderização condicional para estado de erro
  if (error) {
    return <ErrorState message={typeof error === 'string' ? error : 'Ocorreu um erro ao carregar os dados'} />;
  }

  // Renderização condicional para clinicId não encontrado
  if (clinicId === null) {
    return (
      <div className={`p-6 min-h-screen flex items-center justify-center ${isDark ? 'bg-[#111827]' : 'bg-gray-50'}`}>
        <div className={`max-w-lg w-full rounded-2xl shadow-xl border p-6 text-center ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="mb-4 flex justify-center">
            <div className="p-3 bg-amber-100 rounded-full">
              <AlertCircleIcon className="h-8 w-8 text-amber-500" />
            </div>
          </div>
          <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Clínica não identificada</h2>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Não foi possível identificar a clínica atual. Por favor, verifique se você está logado corretamente.
          </p>
        </div>
      </div>
    );
  }

  // Renderização do conteúdo principal
  return (
    <div className={`min-h-screen w-full transition-colors duration-300 ${isDark ? 'bg-[#111827] text-gray-200' : 'bg-gradient-to-b from-gray-50 to-white text-gray-800'}`}>

      {/* HEADER */}
      <header className={`sticky top-0 z-20 border-b px-4 py-3 shadow-sm backdrop-blur mb-6 ${isDark ? 'border-gray-700 bg-[#111827]/90' : 'border-gray-200 bg-white/90'}`}>
        <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Dashboard de Performance</h1>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Visão geral, horários e inteligência artificial</p>
            </div>

            {/* Seletor de filtro mensal */}
            <div className="w-full md:w-auto">
              <MonthlyCalendarFilter
                currentFilter={currentFilter}
                startDate={startDate}
                endDate={endDate}
                onFilterSelect={handleFilterSelect}
                isLoading={loading}
                className="w-full md:w-64"
              />
            </div>
          </div>

          {/* Tabs de navegação - Integradas ao Header */}
          <div className="mt-6 flex space-x-1 overflow-x-auto pb-1 scrollbar-hide">
            {[
              { id: 0, label: 'Visão Geral', icon: BarChart2Icon },
              { id: 1, label: 'Análise de Horários', icon: ClockIcon },
              { id: 2, label: 'IA + Humano', icon: BrainCircuitIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap ${activeTab === tab.id
                    ? isDark
                      ? 'bg-sminy-primary/20 text-sminy-primary ring-1 ring-sminy-primary/50'
                      : 'bg-blue-50 text-blue-600 ring-1 ring-blue-200'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                <tab.icon className={`h-4 w-4 mr-2 ${activeTab === tab.id ? '' : 'opacity-70'}`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Conteúdo das tabs */}
        <div className="mb-8">
          {activeTab === 0 && (
            <OverviewTab
              funnelMetrics={funnelMetrics}
              timeseriesData={timeseriesData}
              periodComparison={periodComparison}
              startDate={new Date(startDate)}
              endDate={new Date(endDate)}
              comparisonStartDate={getFirstDayOfPreviousMonth()}
              comparisonEndDate={new Date(getFirstDayOfPreviousMonth().getFullYear(),
                getFirstDayOfPreviousMonth().getMonth(),
                new Date(endDate).getDate())}
              agendamentosPorDataConsulta={funnelMetrics?.agendamentosPorDataConsulta}
            />
          )}

          {activeTab === 1 && (
            <HorariosTab
              timeDistribution={timeDistribution}
              weeklyHeatmap={weeklyHeatmap}
            />
          )}

          {activeTab === 2 && (
            <AiVsHumanoTab
              data={aiVsHumanoData}
            />
          )}
        </div>

        {/* Rodapé */}
        <footer className={`mt-12 text-center text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          <p>Dashboard de Métricas v2.0 | Sminy AI</p>
        </footer>
      </div>
    </div>
  );
};

export default DashboardMetrics;